import { Component, h, Host, Prop, State, Watch } from "@stencil/core";
import { List, ShoppingListsResponse } from "./shopping-lists-response";
import api from "../../utils/api";
import { sortByAlphabet, sortByDate } from "../../utils/sort-order";

@Component({
    tag: "ux-w-shopping-lists",
    styleUrl: "shopping-lists.scss",
    shadow: false
})
export class ShoppingLists {
    /** URL where this widget can request the data */
    @Prop() url: string = "";
    /** This holds the response from the API with all Information required to show the widget */
    @State() data: ShoppingListsResponse = null;
    /** Array which holds all lists */
    @State() listArray: Array<List> = [];
    /** data is loading */
    @State() loading: boolean = false;

    private handleSortChange = (event: CustomEvent): void => {
        if (
            event.detail === "sortBy:alphabet" ||
            event.detail === "sortBy:alphabet:desc"
        ) {
            this.listArray.sort((a, b) => sortByAlphabet(a.title, b.title));
        } else if (
            event.detail === "sortBy:date" ||
            event.detail === "sortBy:date:desc"
        ) {
            this.listArray.sort((a, b) =>
                sortByDate(a.lastChange, b.lastChange)
            );
        }

        if (
            event.detail === "sortBy:alphabet:desc" ||
            event.detail === "sortBy:date:desc"
        ) {
            this.listArray.reverse();
        }
        // force rerender with following line
        this.listArray = [...this.listArray];
    };

    @Watch("url")
    componentWillLoad() {
        if (this.url === "") {
            // eslint-disable-next-line no-console
            console.warn("url needs to be supplied for WidgetShoppingLists");
            return;
        }
        window.setTimeout(() => {
            if (this.data === null) {
                this.loading = true;
            }
        });
        api<ShoppingListsResponse>(this.url)
            .then((data: ShoppingListsResponse) => {
                this.data = data;
                this.listArray = this.data.lists;
                this.loading = false;
            })
            // eslint-disable-next-line no-console
            .catch((error) => console.error(error));
    }

    render() {
        const baseClass = "ux-w-shopping-lists";
        if (this.loading) {
            return (
                <Host class={baseClass}>
                    <ux-w-wrapper isLoading />
                </Host>
            );
        }
        if (this.data === null) {
            return null;
        }
        const {
            link,
            linkTitle,
            title,
            lists,
            noListsText,
            createListLink,
            createList,
            messages,
            noData,
            noDataText
        } = this.data;
        const hasLists = lists?.length > 0;
        const hasSortableList = lists?.length > 1;
        const hasAtLeastOneSortingValue =
            !!messages?.alphabeticalLabel ||
            !!messages?.alphabeticalDescLabel ||
            !!messages?.recentlyChangedLabel ||
            !!messages?.recentlyChangedDescLabel;
        const hasMenu =
            hasLists &&
            hasSortableList &&
            !!messages?.sortByLabel &&
            hasAtLeastOneSortingValue;

        return (
            <Host class={baseClass}>
                <ux-w-wrapper
                    url={link}
                    linkTitle={linkTitle}
                    headline={title}
                    hasMenu={hasMenu}
                >
                    {hasMenu && (
                        <div slot="menu">
                            <div class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
                                {messages.sortByLabel}
                            </div>
                            <ux-select
                                class="ux-u-text-uppercase"
                                onUxSelectChange={this.handleSortChange}
                            >
                                {messages?.alphabeticalLabel && (
                                    <option value="sortBy:alphabet" selected>
                                        {messages.alphabeticalLabel}
                                    </option>
                                )}
                                {messages?.alphabeticalDescLabel && (
                                    <option value="sortBy:alphabet:desc">
                                        {messages.alphabeticalDescLabel}
                                    </option>
                                )}
                                {messages?.recentlyChangedLabel && (
                                    <option value="sortBy:date">
                                        {messages.recentlyChangedLabel}
                                    </option>
                                )}
                                {messages?.recentlyChangedDescLabel && (
                                    <option value="sortBy:date:desc">
                                        {messages.recentlyChangedDescLabel}
                                    </option>
                                )}
                            </ux-select>
                        </div>
                    )}
                    <div class={`${baseClass}__slot-container`} slot="content">
                        {!hasLists && !noData && (
                            <ux-w-empty
                                link={createListLink}
                                linkText={createList}
                                icon="watchlist"
                            >
                                <div innerHTML={noListsText}></div>
                            </ux-w-empty>
                        )}
                        {!hasLists && noData && (
                            <ux-w-empty icon="crane">
                                <div innerHTML={noDataText}></div>
                            </ux-w-empty>
                        )}
                        {this.listArray?.map((list) => (
                            <div class={`${baseClass}__item`}>
                                <ux-w-shopping-lists-item
                                    headline={list.title}
                                    lastChange={list.lastChange}
                                    lastChangeLabel={messages?.lastChange}
                                    listLink={list.listLink}
                                    listLinkTitle={messages?.details}
                                    articleLabel={messages?.articleLabel}
                                    addListToCartLink={list.addListToCartLink}
                                    addListToCartLinkTitle={
                                        messages?.addListToCart
                                    }
                                    amount={list.amount}
                                />
                            </div>
                        ))}
                    </div>
                </ux-w-wrapper>
            </Host>
        );
    }
}
